import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { FlagIcon } from 'react-flag-kit';
import { SearchableSelect } from '../../shared/layout/forms/AppSelect';

const PhoneNumberInput = ({ countryCodes, phoneValue, phoneCountryValue, onChange }) => {
    const { t } = useTranslation();
    const [selectedCountryCode, setSelectedCountryCode] = useState(phoneCountryValue || '');
    const [phoneNumber, setPhoneNumber] = useState(phoneValue || '');

    useEffect(() => {
        setSelectedCountryCode(phoneCountryValue || '');
        setPhoneNumber(phoneValue || '');
    }, [phoneValue, phoneCountryValue]);

    const handleCountryCodeChange = (e) => {
        const countryCode = e.target.value;
        setSelectedCountryCode(countryCode);
        onChange({
            phoneCountryCode: countryCode,
            phone: phoneNumber,
        });
    };

    const handlePhoneNumberChange = (e) => {
        const number = e.target.value;
        setPhoneNumber(number);
        onChange({
            phoneCountryCode: selectedCountryCode,
            phone: number,
        });
    };

    const formatPhoneCode = (code) => {
        if (!code) return '';
        return code.startsWith('+') ? code : `+${code}`;
    };

    const countryOptions = countryCodes.map((country) => ({
        value: country.id,
        label: country.name,
        iso: country.iso || country.iso2 || country.iso_code || '',
        phonecode: formatPhoneCode(country.phonecode),
    }));

    const selectedOption =
        countryOptions.find((option) => String(option.value) === String(selectedCountryCode)) || null;

    const selectStyles = {
        control: (provided, state) => ({
            ...provided,
            border: '1px solid #ccc',
            boxShadow: state.isFocused ? '0 0 5px rgba(130, 22, 146, 0.5)' : 'none',
            borderColor: state.isFocused ? 'rgba(130, 22, 146, 1)' : '#ccc',
            minHeight: '42px',
        }),
        valueContainer: (provided) => ({
            ...provided,
            padding: '0 0.75rem',
        }),
        placeholder: (provided) => ({
            ...provided,
            color: '#999',
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
        }),
        singleValue: (provided) => ({
            ...provided,
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
        }),
        menu: (provided) => ({
            ...provided,
            zIndex: 9999,
        }),
        menuPortal: (provided) => ({
            ...provided,
            zIndex: 9999,
        }),
    };

    const formatOptionLabel = (option, { context }) => (
        <OptionRow>
            {option.iso ? <FlagIcon code={option.iso.toUpperCase()} size={16} /> : <FlagPlaceholder />}
            <OptionText>
                {context === 'menu'
                    ? `${option.phonecode || ''} ${option.label}`.trim()
                    : (option.phonecode || option.label)}
            </OptionText>
        </OptionRow>
    );

    return (
        <InputGroup>
            <SelectWrapper>
                <SearchableSelect
                    inputId="phone_country_code"
                    placeholder={t('PhoneInput.selectCountryCode')}
                    isClearable
                    isSearchable
                    styles={selectStyles}
                    options={countryOptions}
                    value={selectedOption}
                    formatOptionLabel={formatOptionLabel}
                    menuPortalTarget={document.body}
                    menuPosition="fixed"
                    onChange={(_, nextValue) => handleCountryCodeChange({ target: { value: nextValue || '' } })}
                />
            </SelectWrapper>
            <PhoneNumber
                type="tel"
                value={phoneNumber}
                onChange={handlePhoneNumberChange}
                placeholder={t('PhoneInput.placeholder')}
            />
        </InputGroup>
    );
};

PhoneNumberInput.propTypes = {
    countryCodes: PropTypes.arrayOf(
        PropTypes.shape({
            id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
            name: PropTypes.string.isRequired,
            phonecode: PropTypes.string.isRequired,
        })
    ).isRequired,
    phoneValue: PropTypes.string,
    phoneCountryValue: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    onChange: PropTypes.func.isRequired,
};

export default PhoneNumberInput;

// Styled Components
const InputGroup = styled.div`
    display: grid;
    grid-template-columns: 180px 1fr;
    gap: 0.75rem;
    width: 100%;
    box-sizing: border-box;

    @media (max-width: 768px) {
        grid-template-columns: 1fr;
    }
`;


const SelectWrapper = styled.div`
    width: 100%;
`;

const PhoneNumber = styled.input`
    width: 100%;
    padding: 0.625rem 0.875rem;
    border: 1.5px solid ${({ isInvalid }) => (isInvalid ? '#ef4444' : '#e5e7eb')};
    border-radius: 8px;
    font-size: 0.9375rem;
    width: 100%;
    box-sizing: border-box;
    background: #f9fafb;
    outline: none;
    transition: all 0.2s;

    &:focus {
        border-color: #821692;
        background: #fff;
        box-shadow: 0 0 0 4px rgba(130, 22, 146, 0.1);
    }

    @media (max-width: 768px) {
        max-width: 100%;
    }
`;


const OptionRow = styled.div`
    display: inline-flex;
    align-items: center;
    gap: 0.5rem;
`;

const OptionText = styled.span`
    white-space: nowrap;
`;

const FlagPlaceholder = styled.span`
    width: 16px;
    height: 12px;
    display: inline-block;
    background: rgba(0, 0, 0, 0.08);
    border-radius: 2px;
`;
